import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import {
  ShieldCheck,
  Scale,
  FileText,
  AlertCircle,
  ExternalLink,
  Mail,
} from "lucide-react";
import Aos from "aos";
import "aos/dist/aos.css";
import { useSEO } from "../Pages/Seo";

const Terms = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    Aos.init({
      duration: 500,
      easing: "ease-out-cubic",
      once: true,
      offset: 80,
    });
  }, []);

  useSEO({
    title: "Terms & Conditions | Game Zone Hub",
    description: "Read the Terms and Conditions for Game Zone Hub. Understand our policies regarding free online games, user conduct, and third-party links.",
    keywords: "terms and conditions, game zone hub terms, free online games policy, browser games terms",
    canonical: "https://www.gamezonehub.in/terms",
  });

  const sections = [
    {
      id: "acceptance",
      title: "1. Acceptance of Terms",
      icon: <FileText className="w-5 h-5" />,
      content: (
        <p>
          By accessing and using <strong>Game Zone Hub</strong> (the "Website"), you accept and
          agree to be bound by these Terms and Conditions while you <strong>play free online games</strong>. If you do not agree to these terms, 
          please discontinue use of the website immediately. We reserve the right to modify these terms without prior notice.
        </p>
      ),
    },
    {
      id: "intellectual",
      title: "2. Intellectual Property",
      icon: <ShieldCheck className="w-5 h-5" />,
      content: (
        <>
          <p className="mb-3">
            <strong>Game Zone Hub</strong> acts as a platform for third-party HTML5 <strong>online games</strong>. 
            All games, characters, and graphics belong to their respective developers/publishers 
            and are provided via legitimate game distribution channels.
          </p>
          <p>
            However, the Website's logo, design, layout, code, and original text content are the 
            exclusive intellectual property of <strong>Game Zone Hub</strong>. You may not copy, 
            reproduce, or distribute our branding without permission.
          </p>
        </>
      ),
    },
    {
      id: "user-conduct",
      title: "3. User Conduct",
      icon: <Scale className="w-5 h-5" />,
      content: (
        <div className="space-y-2">
          <p>
            Users agree to use the website only for lawful purposes. While enjoying our <strong>free browser games</strong>, you are strictly prohibited from:
          </p>
          <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-400">
             <li>Using bots, hacks, or cheat software.</li>
             <li>Attempting to compromise site security or DDoS attacks.</li>
             <li>Using ad-blockers that interfere with site revenue generation.</li>
             <li>Posting malicious links or harmful content.</li>
          </ul>
        </div>
      ),
    },
    {
      id: "third-party",
      title: "4. Third-Party Ads & Links",
      icon: <ExternalLink className="w-5 h-5" />,
      content: (
        <p>
          Our Website uses <strong>Google AdSense</strong> and other third-party advertising partners 
          to serve ads. These parties may use cookies to serve ads based on your prior visits. 
          <strong>Game Zone Hub</strong> has no control over the content of these external sites 
          and assumes no responsibility for them. Clicking on ads is at your own risk.
        </p>
      ),
    },
    {
      id: "disclaimer",
      title: "5. Disclaimer & Liability",
      icon: <AlertCircle className="w-5 h-5" />,
      content: (
        <p>
          The Website and all games are provided on an "AS IS" and "AS AVAILABLE" basis. 
          <strong>Game Zone Hub</strong> makes no warranties that the service to <strong>play free online</strong> will be uninterrupted, 
          error-free, or free of viruses. In no event shall we be liable for any direct, indirect, 
          or consequential damages resulting from your use of the website.
        </p>
      ),
    },
    {
      id: "contact", 
      title: "6. Contact Information", 
      icon: <Mail className="w-5 h-5" />,
      content: (
        <p>
          If you have any questions about these Terms, please contact us at: <br />
          <a href="mailto:support@gamezonehub.in" className="text-blue-400 font-semibold cursor-pointer hover:underline">
             support@gamezonehub.in
          </a>
        </p>
      ),
    },
  ];

  return (
      <div className="relative bg-[#0b1120] min-h-screen text-gray-300 sm:pt-[120px] pt-[100px] pb-11">
      <div className="absolute inset-x-0 top-0 h-96 bg-gradient-to-b from-blue-500/10 to-transparent pointer-events-none" />
      

      <div className="container mx-auto px-4 relative z-10">
        
        <div className="text-center md:mb-20 mb-11">
          <h1 className="text-3xl sm:text-4xl md:text-5xl xl:text-6xl font-extrabold mb-3">
            Terms &{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-300">
              Conditions
            </span>
          </h1>

          <p className="text-gray-400 max-w-2xl mx-auto">
            Please read these terms carefully before using our platform.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-10 max-w-7xl mx-auto">
          
          <div className="lg:w-1/3">
            <div
              className="sticky top-28 bg-[#111827]/80 backdrop-blur-md border border-white/10 rounded-2xl p-6 shadow-lg"
              data-aos="fade-right"
            >
              <h3 className="text-white font-bold text-xl mb-4">
                Table of Contents
              </h3>

              <nav className="space-y-1">
                {sections.map((section, i) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(section.id)?.scrollIntoView({ behavior: "smooth" });
                    }}
                    data-aos="fade-up"
                    data-aos-delay={i * 80}
                    className="group flex items-center gap-3 px-4 py-3 rounded-xl text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 transition-all"
                  >
                    <span className="text-blue-500 group-hover:scale-110 transition-transform">
                      {section.icon}
                    </span>
                    <span className="text-sm font-medium">
                      {section.title.split(". ")[1]}
                    </span>
                  </a>
                ))}
              </nav>
            </div>
          </div>

          <div className="lg:w-2/3 space-y-8">
            {sections.map((section, i) => (
              <div
                key={section.id}
                id={section.id}
                data-aos="fade-up"
                data-aos-delay={i * 120}
                className="bg-[#111827] rounded-2xl p-6 sm:p-8 border border-white/5 scroll-mt-32 transition-all duration-300 hover:-translate-y-1 hover:border-blue-500/30 hover:shadow-[0_20px_40px_-20px_rgba(59,130,246,0.4)]"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2.5 bg-blue-500/10 rounded-xl text-blue-400">
                    {section.icon}
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white">
                    {section.title}
                  </h2>
                </div>

                <div className="text-gray-300 leading-relaxed text-sm sm:text-base">
                  {section.content}
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>

    </div>
  );
};

export default Terms;